<script>
import AuthWrapper from '@/components/auth/AuthWrapper.vue';
import authStorage from '@/shared/services/auth-storage.service';
import { mapActions } from 'vuex';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The API answers a failed login with `Invalid credentials`, in English, and a
 * malformed payload with the class-validator messages - also English. Neither
 * is showable, so the status decides the wording and the response body is only
 * ever used to tell the cases apart.
 */
function authErrorMessage(err) {
  if (!err.response) {
    return 'Serveur injoignable. Veuillez réessayer dans un instant.';
  }

  switch (err.response.status) {
    case 400:
    case 401:
      return 'Email ou mot de passe incorrect.';
    case 403:
      return "Votre compte n'est pas autorisé à se connecter.";
    case 429:
      return 'Trop de tentatives. Veuillez patienter avant de réessayer.';
    default:
      return 'Connexion impossible pour le moment. Veuillez réessayer.';
  }
}

export default {
  components: {
    AuthWrapper,
  },
  data() {
    return {
      togglePassword: false,
      email: '',
      password: '',
      rememberMe: false,
      processing: false,
      authError: null,
      fieldErrors: {
        email: null,
        password: null,
      },
    };
  },
  watch: {
    // The message now sits under the two fields it accuses, so editing either
    // one makes it stale: drop it rather than leave a red line under a field
    // that has since changed.
    email() {
      this.authError = null;
    },
    password() {
      this.authError = null;
    },
  },
  created() {
    // Restore what the previous "Se souvenir de moi" left behind.
    this.rememberMe = authStorage.isRemembered();
    this.email = authStorage.getRememberedEmail();
  },
  methods: {
    ...mapActions('auth', ['login']),

    validate() {
      const email = this.email.trim();
      const errors = { email: null, password: null };

      if (!email) {
        errors.email = 'Veuillez saisir votre email.';
      } else if (!EMAIL_PATTERN.test(email)) {
        errors.email = 'Veuillez saisir un email valide.';
      }

      if (!this.password) {
        errors.password = 'Veuillez saisir votre mot de passe.';
      }

      this.fieldErrors = errors;

      return !errors.email && !errors.password;
    },

    async handleLogin() {
      this.authError = null;

      if (!this.validate()) {
        return;
      }

      const email = this.email.trim();
      this.processing = true;

      try {
        // Recorded before the call so the tokens the login writes land in
        // localStorage (checked) or sessionStorage (unchecked).
        authStorage.setRemembered(this.rememberMe);

        await this.login({ email, password: this.password });

        authStorage.setRememberedEmail(email);

        this.$router.push({ path: '/' });
      } catch (err) {
        this.authError = authErrorMessage(err);
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<template>
  <AuthWrapper variant="form">
    <BCard no-body class="auth-page__card">
      <BCardBody class="auth-page__card-body">
        <div class="text-center">
          <div class="auth-page__logo mb-3">
            <img src="@/assets/images/logo_apii.png" alt="Logo APII" />
          </div>
          <h1 class="auth-form__title">Bienvenue à nouveau !</h1>
          <p class="auth-form__subtitle">
            Connectez-vous pour accéder à la plateforme des enquêtes
          </p>
        </div>

        <form novalidate @submit.prevent="handleLogin">
          <div class="auth-form__fields">
            <div class="auth-form__field">
              <label class="auth-form__label" for="email">Email</label>
              <input
                id="email"
                v-model="email"
                type="email"
                class="form-control"
                :class="{ 'is-invalid': fieldErrors.email || authError }"
                :aria-invalid="Boolean(fieldErrors.email || authError)"
                placeholder="Entrer votre email"
                autocomplete="email"
              />
              <span v-if="fieldErrors.email" class="auth-form__error">
                {{ fieldErrors.email }}
              </span>
            </div>

            <div class="auth-form__field">
              <label class="auth-form__label" for="password-input"
                >Mot de passe</label
              >
              <div class="position-relative">
                <input
                  id="password-input"
                  v-model="password"
                  :type="togglePassword ? 'text' : 'password'"
                  class="form-control auth-form__password-input"
                  :class="{ 'is-invalid': fieldErrors.password || authError }"
                  :aria-invalid="Boolean(fieldErrors.password || authError)"
                  placeholder="Entrer votre mot de passe"
                  autocomplete="current-password"
                />
                <button
                  type="button"
                  class="auth-form__password-toggle"
                  :aria-label="
                    togglePassword
                      ? 'Masquer le mot de passe'
                      : 'Afficher le mot de passe'
                  "
                  :aria-pressed="togglePassword"
                  @click="togglePassword = !togglePassword"
                >
                  <i
                    :class="togglePassword ? 'ri-eye-off-fill' : 'ri-eye-fill'"
                  ></i>
                </button>
              </div>
              <span v-if="fieldErrors.password" class="auth-form__error">
                {{ fieldErrors.password }}
              </span>
            </div>

            <p v-if="authError" class="auth-form__auth-error" role="alert">
              <i class="ri-error-warning-line" aria-hidden="true"></i>
              <span>{{ authError }}</span>
            </p>
          </div>

          <div class="auth-form__options">
            <label class="auth-form__remember" for="remember-check">
              <input id="remember-check" v-model="rememberMe" type="checkbox" />
              <span>Se souvenir de moi</span>
            </label>
            <router-link to="/forgot-password" class="auth-form__link">
              Mot de passe oublié ?
            </router-link>
          </div>

          <button
            class="auth-form__submit"
            type="submit"
            :disabled="processing"
          >
            {{ processing ? 'Connexion…' : 'Se connecter' }}
          </button>
        </form>
      </BCardBody>
    </BCard>
  </AuthWrapper>
</template>
